import { FormControl } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { DateValidationError } from '@mui/x-date-pickers/models';
import { getIn } from 'formik';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { DATE_FORMAT, FormBodyAPI } from 'src/constants';
import { VariableContractField } from 'src/entities/Contract';
import { RecipientSelector } from 'src/features/recipient-selector/RecipientSelector';
import { ResizableTextField } from 'src/legacy/components/TextField';
import {
  DateTextField,
  getDateErrorMessage,
  parseDateValue,
} from 'src/legacy/components/UI/Date/useAddDateForm';
import { FieldType } from 'src/services/api';
import { BlackHeadings, GraySmall } from 'src/theme/colors';
import { ensureUnreachable } from 'src/utils/common_utils';

interface useShareContractParams {
  variableInputs: VariableContractField[];
  selectedTemplateRef: string;
}

const useStyles = makeStyles()((theme) => ({
  copyText: {
    wordBreak: 'break-all',
    color: BlackHeadings,
  },
  iconButton: {
    padding: 0,
  },
  copyIcon: {
    fontSize: 12,
  },
  inputs: {
    marginTop: theme.spacing(2),
  },
  muted: {
    color: GraySmall,
  },
  allClientSelector: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
}));

interface ShareContractFormBodyProps extends FormBodyAPI {
  variableInputs: VariableContractField[];
}

const ShareContractFormBody: FC<ShareContractFormBodyProps> = ({
  variableInputs,
  values,
  errors,
  handleBlur,
  handleChange,
  setFieldValue,
  touched,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [dateErrors, setDateErrors] = useState<
    Record<string, DateValidationError>
  >({});
  const hasVariableInputs = variableInputs?.length > 0;

  const renderVariableInputs = () =>
    variableInputs.map((input) => {
      const fieldName = `fields.${input.id}`;
      const fieldError = getIn(errors, fieldName);
      const fieldTouched = getIn(touched, fieldName);
      const formikError =
        fieldTouched && typeof fieldError === 'string' ? fieldError : '';

      switch (input.type) {
        case FieldType.Date: {
          const pickerError = getDateErrorMessage(dateErrors[input.id]);
          const fieldValue = values.fields[input.id];
          const helperText = pickerError || formikError;
          return (
            <FormControl fullWidth className={classes.inputs} key={input.id}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label={input.label}
                  name={fieldName}
                  format={DATE_FORMAT}
                  value={parseDateValue(fieldValue)}
                  onChange={(val) => {
                    setFieldValue(
                      fieldName,
                      val && val.isValid() ? val.format(DATE_FORMAT) : null,
                    );
                    // MUI X doesn't reliably refire onError(null) on full clear,
                    // so reset the picker error here when the input is emptied.
                    if (val === null) {
                      setDateErrors((prev) => {
                        const next = { ...prev };
                        delete next[input.id];
                        return next;
                      });
                    }
                  }}
                  onError={(reason) =>
                    setDateErrors((prevDateErrors) => ({
                      ...prevDateErrors,
                      [input.id]: reason,
                    }))
                  }
                  // required because the `textField` slot renders a plain <input />; MUI X v7+ defaults to PickersSectionList
                  enableAccessibleFieldDOMStructure={false}
                  slots={{ textField: DateTextField }}
                  slotProps={{
                    textField: {
                      name: fieldName,
                      variant: 'outlined',
                      margin: 'none',
                      onBlur: handleBlur,
                      error: Boolean(helperText),
                      helperText,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
          );
        }
        case FieldType.Text:
          return (
            <FormControl fullWidth className={classes.inputs} key={input.id}>
              <ResizableTextField
                variant="outlined"
                type="text"
                label={input.label}
                name={fieldName}
                value={values.fields[input.id]}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(formikError)}
                helperText={formikError}
              />
            </FormControl>
          );
        case FieldType.Signature:
        case FieldType.Initials:
          return null;
        default:
          return ensureUnreachable(input.type);
      }
    });

  return (
    <div>
      <FormControl fullWidth className={classes.inputs}>
        <RecipientSelector
          ignoreFilterClientsByCompany
          recipientTypes={['client']}
          onRecipientSelected={(vals) => {
            const clientCompanyIds = vals.map((val) => ({
              clientId: val.id,
              companyId: val.companyId,
            }));
            setFieldValue('recipientIds', clientCompanyIds);
          }}
          name="contractRecipientIds"
          placeholder={t(($) => $.actions.select_clients)}
          error={Boolean(errors.recipientIds)}
          helperText={errors.recipientIds}
        />
      </FormControl>
      {hasVariableInputs && <div>{renderVariableInputs()}</div>}
    </div>
  );
};

export const useShareContract = ({
  variableInputs,
}: useShareContractParams) => {
  const { t } = useTranslation();
  // Creating validation schema for all the variable inputs
  // We want all the variable inputs to be required
  const generateValidationSchema = useMemo(() => {
    const yupSchema: Record<
      string,
      Yup.StringSchema<string> | Yup.DateSchema<Date>
    > = {};
    if (variableInputs?.length > 0) {
      variableInputs.forEach((input) => {
        if (input.id)
          yupSchema[input.id] =
            input.type === FieldType.Date
              ? Yup.date()
                  .typeError('Invalid date')
                  .required('Field is required')
              : Yup.string()
                  .required('Field is required')
                  .max(1000, "Field can't be more than 1000 characters");
      });
    }
    return yupSchema;
  }, [variableInputs]);

  const validationScheme = Yup.object().shape({
    recipientIds: Yup.array()
      .of(
        Yup.object().shape({
          clientId: Yup.string(),
          companyId: Yup.string(),
        }),
      )
      .min(
        1,
        t(($) => $.validation.channels.select_at_least_one_client),
      )
      .required(t(($) => $.validation.channels.select_at_least_one_client)),
    fields: Yup.object().shape(generateValidationSchema),
  });

  const FormRenderer = useCallback(
    (props: FormBodyAPI) => (
      <ShareContractFormBody {...props} variableInputs={variableInputs} />
    ),
    [variableInputs],
  );

  return { validationScheme, FormRenderer };
};
